<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { isAdmin, isDevUser, selectedAdmin } from '@/stores/appConfig'

const router = useRouter()

const contractorCards = [
  { title: 'CRM',       icon: 'mdi-account-group-outline', to: '/crm/leads', description: 'Leads, contacts, and customer conversations.' },
  { title: 'Quotes',    icon: 'mdi-currency-usd',      to: '/quote',     description: 'Create estimates, send quotes, and track approvals.', devOnly: true },
  { title: 'Jobs',      icon: 'mdi-briefcase-outline', to: '/jobs',      description: 'Manage booked work, schedules, and job status.' },
  { title: 'Marketing', icon: 'mdi-bullhorn-outline',  to: '/marketing/digital', description: 'Ad performance, print design, reviews, and campaigns.' },
  { title: 'CMS',       icon: 'mdi-web',               to: '/cms',       description: 'Edit your website, publishing, and domain setup.' },
]

const adminCards = [
  { title: 'Users',  icon: 'mdi-account-group-outline',  section: '/admin/users',  description: 'Users, companies, websites & team members' },
  { title: 'Admins', icon: 'mdi-shield-account-outline', section: '/admin/admins', description: 'Platform administrator accounts' },
  { title: 'Logs',   icon: 'mdi-history',                section: '/admin/logs',   description: 'User activity history' },
]

const visibleContractorCards = computed(() =>
  contractorCards.filter(card => !card.devOnly || isDevUser.value)
)

function goToAdminSection(section) {
  selectedAdmin.value = section
  router.push('/admin')
}
</script>

<template>
  <main class="home-page">
    <section class="home-shell">

      <!-- Admin dashboard -->
      <template v-if="isAdmin">
        <header class="home-header">
          <p class="eyebrow">Platform</p>
          <h1>Administration</h1>
        </header>

        <div class="app-grid">
          <button
            v-for="card in adminCards"
            :key="card.title"
            class="app-card app-card--admin"
            type="button"
            @click="goToAdminSection(card.section)"
          >
            <span class="app-icon">
              <v-icon size="38">{{ card.icon }}</v-icon>
            </span>
            <div>
              <span class="app-title">{{ card.title }}</span>
              <span class="app-desc">{{ card.description }}</span>
            </div>
          </button>
        </div>
      </template>

      <!-- Contractor dashboard -->
      <template v-else>
        <div class="app-grid">
          <RouterLink
            v-for="app in visibleContractorCards"
            :key="app.title"
            class="app-card app-card--admin app-card--contractor"
            :to="app.to"
          >
            <span class="app-icon">
              <v-icon size="38">{{ app.icon }}</v-icon>
            </span>
            <div>
              <span class="app-title">{{ app.title }}</span>
              <span class="app-desc">{{ app.description }}</span>
            </div>
          </RouterLink>
        </div>
      </template>

    </section>
  </main>
</template>

<style scoped>
.home-page {
  min-height: calc(100dvh - 70px);
  padding: 32px 18px 110px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.home-shell {
  width: min(980px, 100%);
  margin: 0 auto;
}

.home-header {
  margin-bottom: 28px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.home-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(166px, 1fr));
  gap: 16px;
}

.app-card {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 18px;
  color: grey;
  text-decoration: none;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(23, 35, 31, 0.06);
  transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}

.app-card--admin {
  aspect-ratio: unset;
  min-height: 140px;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 14px;
  color: var(--text-primary);
  font: inherit;
  cursor: pointer;
  text-align: left;
}

.app-card:hover,
.app-card:focus-visible {
  transform: translateY(-3px);
  border-color: rgba(31, 122, 99, 0.35);
  box-shadow: 0 18px 38px rgba(23, 35, 31, 0.11);
  outline: none;
}

.app-icon {
  width: 51px;
  height: 51px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f26e2e;
  flex-shrink: 0;
}

.app-title {
  display: block;
  font-size: 1rem;
  font-weight: 850;
}

.app-desc {
  display: block;
  margin-top: 4px;
  color: var(--text-secondary);
  font-size: 0.82rem;
  font-weight: 500;
}

@media (max-width: 620px) {
  .home-page {
    padding: 22px 14px 110px;
  }

  .app-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .app-card {
    padding: 14px;
  }

  .app-icon {
    width: 54px;
    height: 54px;
  }
}
</style>
